/*
 * speaker-diarization.js — browser speaker diarization ("who spoke when") as a
 * POST-PROCESS over an existing transcript. Opt-in; lazy-loads one model.
 *
 * Approach (chosen after A/B testing three candidates on real 2-/3-speaker audio,
 * 2026-07-05):
 *   - lexical (pause + text-similarity, from auto-captions): FAILED — labelled a
 *     clear A/B/A file as one speaker (no pause between turns; English text is too
 *     self-similar to separate voices).
 *   - pyannote-segmentation + per-slice embed + cluster: OVER-SEGMENTED — 4
 *     "speakers" on a 2-speaker file (local per-window ids + noisy short slices).
 *   - WINNER — embed the WHISPER CHUNKS themselves (already speech-aligned, 2-8 s,
 *     clean) with a speaker-embedding model, then single-linkage agglomerative
 *     clustering by cosine. Measured cos: same speaker 0.55-0.87, different
 *     speaker ~0. Result on ground-truth files: 1 voice→1, 2→2 (A/A/B/B/A/A
 *     exact), 3→3. No pyannote needed. Works on the MONO MIX, so a dual-channel
 *     call (A active on its turn, B on theirs) diarizes without a second pass.
 *
 * Model: onnx-community/wespeaker-voxceleb-resnet34-LM (256-dim, ~loads under our
 * CSP, ~6 s once, ~1 s inference for a short file). Loaded on demand so only users
 * who ask for speaker labels pay the download.
 *
 * window.SpeakerDiarization.diarize(audioPcm16k, segments, opts) → resolves with
 *   { speakers: N, labels: [speakerIndex per segment] }  (mutates nothing).
 *   opts: { sampleRate=16000, threshold=0.4, numSpeakers, onProgress(frac,text) }
 *
 * numSpeakers (19.10.2026). The page has always ASKED «Сколько человек говорит»
 * — and only the server lane listened. Here the field did nothing: the same
 * person who typed «2» got five voices. Now, when the clustering finds MORE
 * voices than the person named, the two closest clusters (cosine of their mean
 * embeddings) are merged until the named number is reached. Fewer is left as
 * found: a voice the model could not tell apart cannot be invented. Labels are
 * renumbered by first appearance, as before («Говорящий 1» speaks first).
 */
(function () {
    'use strict';

    var MODEL_ID = 'onnx-community/wespeaker-voxceleb-resnet34-LM';
    var _mod = null, _model = null, _proc = null, _loading = null;

    function transformersUrl() {
        var origin = (typeof location !== 'undefined' && location.origin) || '';
        // См. whisper-worker.js: версия в URL обязательна, файл кладётся
        // вручную и отдаётся immutable на 180 дней.
        return origin + '/vendor/transformers/transformers.min.js?v=3.8.1-esm2';
    }

    async function ensureLoaded(onProgress) {
        if (_model && _proc) return;
        if (_loading) return _loading;
        _loading = (async function () {
            if (onProgress) onProgress(0.05, 'load');
            _mod = _mod || await import(transformersUrl());
            try {
                if (_mod.env) {
                    _mod.env.allowLocalModels = false;
                    if (_mod.env.backends && _mod.env.backends.onnx && _mod.env.backends.onnx.wasm) {
                        _mod.env.backends.onnx.wasm.wasmPaths = (location.origin || '') + '/vendor/transformers/';
                    }
                }
            } catch (_) {}
            _model = await _mod.AutoModel.from_pretrained(MODEL_ID, { dtype: 'fp32' });
            _proc = await _mod.AutoProcessor.from_pretrained(MODEL_ID);
            if (onProgress) onProgress(0.35, 'loaded');
        })();
        try { await _loading; } finally { _loading = null; }
    }

    function cos(a, b) {
        var d = 0, na = 0, nb = 0, n = Math.min(a.length, b.length);
        for (var i = 0; i < n; i++) { d += a[i] * b[i]; na += a[i] * a[i]; nb += b[i] * b[i]; }
        if (!na || !nb) return 0;
        return d / Math.sqrt(na * nb);
    }

    async function embed(slice, sr) {
        var inp = await _proc(slice, { sampling_rate: sr });
        var out = await _model(inp);
        var e = out.embeddings || out.logits || (out.last_hidden_state) || Object.values(out)[0];
        return e && e.data ? Array.from(e.data) : e;
    }

    /* Single-linkage agglomerative clustering: a unit joins the cluster it is most
       similar to (max cosine to ANY member) if that similarity clears the
       threshold, else starts a new cluster. Robust to within-speaker variation
       (a chain of chunks keeps a speaker together) while separating distinct
       voices (cross-speaker cosine ~0). */
    function cluster(units, threshold) {
        var clusters = [];
        for (var u = 0; u < units.length; u++) {
            var best = -1, bi = -1;
            for (var i = 0; i < clusters.length; i++) {
                var mx = -1;
                for (var m = 0; m < clusters[i].length; m++) mx = Math.max(mx, cos(units[u].emb, clusters[i][m].emb));
                if (mx > best) { best = mx; bi = i; }
            }
            if (best > threshold) { clusters[bi].push(units[u]); units[u].spk = bi; }
            else { clusters.push([units[u]]); units[u].spk = clusters.length - 1; }
        }
        return clusters.length;
    }

    function meanOf(members) {
        var dim = members[0].emb.length, c = new Array(dim).fill(0);
        for (var m = 0; m < members.length; m++) {
            for (var d = 0; d < dim; d++) c[d] += members[m].emb[d];
        }
        return c;
    }

    /* Merge the closest clusters until `want` remain, then relabel the units in
       the order their cluster first appears. Pure: works on `units[].spk` and
       `units[].emb`, returns the new speaker count. Exposed for the unit test. */
    function mergeTo(units, want) {
        var by = {};
        for (var u = 0; u < units.length; u++) (by[units[u].spk] = by[units[u].spk] || []).push(units[u]);
        var groups = Object.keys(by).map(function (k) { return by[k]; });
        while (want >= 1 && groups.length > want) {
            var cs = groups.map(meanOf), bi = 0, bj = 1, best = -2;
            for (var i = 0; i < groups.length; i++) {
                for (var j = i + 1; j < groups.length; j++) {
                    var c = cos(cs[i], cs[j]);
                    if (c > best) { best = c; bi = i; bj = j; }
                }
            }
            groups[bi] = groups[bi].concat(groups[bj]);
            groups.splice(bj, 1);
        }
        for (var g = 0; g < groups.length; g++) {
            for (var x = 0; x < groups[g].length; x++) groups[g][x].spk = 'g' + g;
        }
        var order = {}, n = 0;
        for (var v = 0; v < units.length; v++) {
            if (!(units[v].spk in order)) order[units[v].spk] = n++;
            units[v].spk = order[units[v].spk];
        }
        return n;
    }

    var API = {
        mergeTo: mergeTo,
        isSupported: function () {
            try { return typeof AudioContext === 'function' || typeof webkitAudioContext === 'function'; }
            catch (_) { return false; }
        },
        /* audioPcm: Float32Array at sampleRate. segments: [{timestamp|start, end}]
           (whisper chunk shape or transcriptSegment shape). Returns speaker index
           per segment; segments too short to embed inherit the previous label. */
        diarize: async function (audioPcm, segments, opts) {
            opts = opts || {};
            var sr = opts.sampleRate || 16000;
            var threshold = (typeof opts.threshold === 'number') ? opts.threshold : 0.4;
            var onProgress = opts.onProgress || function () {};
            if (!segments || segments.length < 2) {
                return { speakers: segments && segments.length ? 1 : 0, labels: (segments || []).map(function () { return 0; }) };
            }
            await ensureLoaded(onProgress);

            var units = [], idxMap = [];
            for (var i = 0; i < segments.length; i++) {
                var seg = segments[i];
                var st = (seg.timestamp != null ? (Array.isArray(seg.timestamp) ? seg.timestamp[0] : seg.timestamp) : seg.start) || 0;
                var en = (seg.end != null ? seg.end : (Array.isArray(seg.timestamp) ? seg.timestamp[1] : null));
                if (en == null || !(en > st)) en = st + 2;
                if (en - st < 0.5) { idxMap.push(-1); continue; }   /* too short — inherit neighbour */
                var s0 = Math.max(0, Math.floor(st * sr));
                var s1 = Math.min(audioPcm.length, Math.floor(en * sr));
                if (s1 - s0 < sr * 0.4) { idxMap.push(-1); continue; }
                var emb = await embed(audioPcm.slice(s0, s1), sr);
                idxMap.push(units.length);
                units.push({ emb: emb });
                onProgress(0.35 + 0.6 * (i / segments.length), 'embed');
            }
            if (!units.length) return { speakers: 1, labels: segments.map(function () { return 0; }) };

            var speakers = cluster(units, threshold);
            var want = opts.numSpeakers | 0;
            if (want >= 2 && speakers > want) speakers = mergeTo(units, want);

            /* Map back to every segment; short/skipped ones inherit the previous
               labelled segment (or the next, for a leading short one). */
            var labels = new Array(segments.length);
            var lastLabel = 0;
            for (var j = 0; j < segments.length; j++) {
                if (idxMap[j] >= 0) { lastLabel = units[idxMap[j]].spk; labels[j] = lastLabel; }
                else labels[j] = lastLabel;
            }
            for (var k = 0; k < segments.length && idxMap[k] < 0; k++) {
                /* leading short segments: borrow the first real label */
                var firstReal = labels.find(function (_, ix) { return idxMap[ix] >= 0; });
                labels[k] = (typeof firstReal === 'number') ? firstReal : 0;
            }
            onProgress(1, 'done');
            return { speakers: speakers, labels: labels };
        }
    };

    if (typeof window !== 'undefined') window.SpeakerDiarization = API;
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
})();
